import { SmsCommands } from '@harvestiq/constants';
import { Checkbox } from './ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel } from './ui/form';
import { Control, FieldValues, Path } from 'react-hook-form';

type SmsOptInCheckboxProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  disabled?: boolean;
  orgName: string;
  tosLink: string;
  privacyPolicyLink: string;
  isThirdPerson?: boolean;
};

export function SmsOptInCheckbox<TFieldValues extends FieldValues>({
  control,
  name,
  disabled = false,
  orgName,
  tosLink,
  privacyPolicyLink,
  isThirdPerson = false,
}: SmsOptInCheckboxProps<TFieldValues>) {
  const labelText = isThirdPerson
    ? `User agrees to receive marketing text messages from ${orgName} based on their information.`
    : `I agree to receive marketing text messages from ${orgName} based on my information.`;

  const descriptionText = isThirdPerson
    ? `By checking this, the user agrees to receive marketing messages from ${orgName} sent via an autodialer, and this agreement isn't a condition of any purchase. The user also agrees to the`
    : `By checking this you agree to receive marketing messages from ${orgName} sent via an autodialer, and this agreement isn't a condition of any purchase. You also agree to the`;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={disabled}
            />
          </FormControl>
          <div className="space-y-1 leading-none">
            <FormLabel className="text-sm font-normal">{labelText}</FormLabel>
            <p className="text-sm text-muted-foreground">
              {descriptionText}{' '}
              <a
                href={tosLink}
                rel="noopener noreferrer"
                target="_blank"
                className="cursor-pointer"
              >
                Terms of Service{' '}
              </a>
              and{' '}
              <a
                href={privacyPolicyLink}
                rel="noopener noreferrer"
                target="_blank"
                className="cursor-pointer"
              >
                Privacy Policy
              </a>
              . Message and Data Rates may apply.{' '}
              {isThirdPerson ? 'The user' : 'You'} can reply with "
              {SmsCommands.MUTE_QUOTES}" or uncheck this box to opt out anytime.
            </p>
            {isThirdPerson && (
              <p className="text-sm text-amber-600 font-semibold mt-2">
                ⚠️ IMPORTANT: By checking this box, you certify that you have
                obtained express consent from the user to receive SMS marketing
                messages. You and your agency assume full responsibility and
                liability for any charges, penalties, or legal consequences
                resulting from non-compliance with SMS consent regulations.
              </p>
            )}
          </div>
        </FormItem>
      )}
    />
  );
}
